import { isString } from "lodash";
import { motion } from "motion/react";
import { CSSProperties, ReactNode, SyntheticEvent, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/core/helpers/cn";
import Icon from "@/shared/ui/Icon";
import SoundButton from "@/shared/ui/button/SoundButton";

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
  classNameOverlay?: string;
  styleOverlay?: CSSProperties;
  className?: string;
  classNameChildren?: string;
  classNameTitle?: string;
  style?: CSSProperties;
  overlay?: boolean;
  title?: ReactNode | string;
  footer?: ReactNode;
  header?: ReactNode;
  navigation?: ReactNode;
  onKeyDown?: (e: KeyboardEvent) => void;
  onKeyUp?: (e: KeyboardEvent) => void;
  modalKey?: string;
  disableAnimation?: boolean;
  container?: string;
  dataMzAccent?: string;
  classNameCloseBtn?: string;
};

export default function Modal({
  open,
  children,
  classNameOverlay,
  styleOverlay,
  className,
  classNameChildren,
  classNameTitle,
  style,
  onClose,
  overlay = true,
  title,
  footer,
  header,
  navigation,
  onKeyDown,
  onKeyUp,
  modalKey,
  disableAnimation,
  container,
  dataMzAccent,
  classNameCloseBtn,
}: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const refOverlay = useRef<HTMLDivElement>(null);

  const closeByEscHandler = useCallback(
    (e: KeyboardEvent) => {
      // TODO: закоментил потомучто не работал backspace при вводе кода тфа в настройках аккаунты
      // e.stopPropagation();
      // if (![KeyCode.ESCAPE].includes(e.keyCode)) {
      //   return;
      // }
      // switch (e.keyCode) {
      //   case KeyCode.ESCAPE:
      //     e.preventDefault();
      //     onClose();
      //     break;
      //   default:
      //     break;
      // }
    },
    [onClose],
  );

  useEffect(() => {
    if (refOverlay.current) {
      refOverlay.current.addEventListener("keydown", closeByEscHandler);
      refOverlay.current.addEventListener("keydown", onKeyDown);
      refOverlay.current.addEventListener("keyup", onKeyUp);

      return () => {
        if (refOverlay.current) {
          refOverlay.current.removeEventListener("keydown", closeByEscHandler);
          refOverlay.current.removeEventListener("keydown", onKeyDown);
          refOverlay.current.removeEventListener("keyup", onKeyUp);
        }
      };
    }

    return () => {};
  }, [closeByEscHandler, onKeyDown, onKeyUp, refOverlay]);

  const closeButton = (
    <SoundButton
      color="gray"
      variant="soft"
      className={cn(
        "flex items-center justify-center size-8 p-0 absolute top-2.5 right-2.5",
        classNameCloseBtn,
      )}
      onClick={onClose}
    >
      <Icon name="close" className="w-3" />
    </SoundButton>
  );

  const titleComponent = isString(title) ? <div className="text-18 font-bold">{title}</div> : title;

  if (!open) return null;

  return createPortal(
    <motion.div
      initial={{
        opacity: 0,
      }}
      animate={{ opacity: 1 }}
      transition={{
        duration: disableAnimation ? 0 : 0.2,
      }}
      exit={{ opacity: 0 }}
      ref={refOverlay}
      className={cn(
        "flex flex-col items-center justify-center max-h-[100vh] z-50",
        "fixed top-0 right-0 bottom-0 left-0 isolate",
        overlay ? "bg-dark-900/90" : "",
        "[--scrollbar-my:0.875rem] [--scrollbar-track-bg:#130F144D] [--scrollbar-thumb-bg:#AB2929] [scrollbar-gutter:stable]",
        classNameOverlay,
      )}
      style={{ padding: "calc(var(--boundary-offset-y))", ...styleOverlay }}
      onClick={(event: SyntheticEvent) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          onClose();
        }
      }}
    >
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-4 relative w-[32.5rem] overflow-clip",
          "p-7 bg-dark-900 rounded-[0.875rem]",
          className,
        )}
        style={{
          maxHeight: "calc(100vh - 2 * var(--boundary-offset-y))",
          boxShadow: "0px 0px 200px 0px rgba(171, 41, 41, 0.60)",
          ...style,
        }}
        data-mz-accent={dataMzAccent}
      >
        {closeButton}

        {title ? (
          <div className={cn("flex items-start", classNameTitle)}>{titleComponent}</div>
        ) : null}

        {header}
        <div
          className={cn(
            "grow overflow-y-auto px-7 -mx-7",
            "[--scrollbar-my:0.875rem] [--scrollbar-track-bg:#130F144D] [--scrollbar-thumb-bg:#AB2929] [scrollbar-gutter:stable]",
            classNameChildren,
          )}
        >
          {children}
        </div>
        {footer}
      </div>
      {/* TODO: надо удалить чтобы облегчить Modal */}
      {navigation && navigation}
    </motion.div>,
    document.getElementById(container || "root"),
    modalKey,
  );
}
